import { LitElement } from 'lit'
import { i18n } from '@i18n/i18n'
import { SignalWatcher } from '@lit-labs/preact-signals'
import { connectedPerson } from '@src/models/signals'
import { CustomEvents } from '@src/models/events'
import { auths } from '@shared/authorisations'
//import { timeConsole } from '@utils/time-console'

export class Elm extends SignalWatcher(LitElement) {
	labelGroup = ''

	createRenderRoot(): HTMLElement {
		return this // turn off shadow dom to access external styles
	}

	// Lit still runs an update that was pending when the element left the DOM. Through the signal
	// watcher that update would subscribe the detached element to every signal it reads, with nothing
	// left to unsubscribe it: it would re-render on each change for the rest of the session. Such an
	// update runs unwatched.
	protected performUpdate(): void {
		if (this.isConnected) super.performUpdate()
		else (LitElement.prototype as Elm).performUpdate.call(this)
	}

	dispatch(eventName: CustomEvents, detail?: Object) {
		this.dispatchEvent(
			new CustomEvent(eventName, {
				detail,
				bubbles: true,
			})
		)
	}

	private debounceTimer: number | undefined
	/** Executes a callback after a debounce delay: `this.debounce(() => save())` */
	debounce(cb: () => void, duration = 300) {
		if (this.debounceTimer !== undefined) {
			clearTimeout(this.debounceTimer)
		}
		this.debounceTimer = window.setTimeout(cb.bind(this), duration)
	}

	/** Returns a debounced function — use for event handler references: `@input=${this.debounceHandler(fn)}` */
	debounceHandler(cb: (...args: any[]) => void, duration = 300): (...args: any[]) => void {
		return (...args: any[]): void => {
			this.debounce(() => cb.apply(this, args), duration)
		}
	}

	t(key: string, group?: string) {
		// Text reactivity now flows through the i18n.texts signal (read in bb_t → getText),
		// so components re-render when labels load or the language changes — no need to also
		// subscribe to the `lang` signal here (#1513).
		return bb_t(key, group || this.labelGroup, this.tagName)
	}

	getFormJSON = (form: HTMLFormElement) => {
		// get all form data
		const data = new FormData(form)
		// Array.from to FormData.keys => Array, then reduce it to an object.
		return Array.from(data.keys()).reduce((result: { [key: string]: string }, key) => {
			// For each key, get all values (multiple possible for select-multiple, checkboxes, etc.)
			const value = data.getAll(key)
			// join multiple into a single comma-separated string
			result[key] = value.length > 1 ? value.join(', ') : String(value[0])
			return result
		}, {})
	}

	hasAuth(authName: string) {
		const authorisations = connectedPerson.value?.role.authorisations
		return !!authorisations?.some(
			(authorisation) => authorisation.name === auths.Admin || authorisation.name === authName
		)
	}

	protected logTime(_label: string): void {
		// Try to detect which lifecycle method we're in
		/*const stack = new Error().stack
		let lifecycle = 'unknown'

		if (stack) {
			const lines = stack.split('\n')
			// Look for lifecycle method names in stack
			const lifecycleLine = lines.find(
				(line) =>
					line.includes('.render') ||
					line.includes('.updated') ||
					line.includes('.firstUpdated') ||
					line.includes('.willUpdate') ||
					line.includes('.connectedCallback') ||
					line.includes('.disconnectedCallback')
			)

			if (lifecycleLine) {
				const match = lifecycleLine.match(
					/\.(render|updated|firstUpdated|willUpdate|connectedCallback|disconnectedCallback)/
				)
				if (match) {
					lifecycle = match[1]
				}
			}
		}
		// TIME CONSOLE instance if we whant to use it inside elm components as this.timeConsole.log...
		// Get component name from tag name
		const componentName = this.tagName.toLowerCase()
		const source = `${componentName}.${lifecycle}`
		timeConsole.log(label, source)
		*/
	}
}

export function bb_t(key: string, group?: string, tagName: string = ''): string {
	//text function to use outside web component, eg: in a utility function
	function camelize(str: string): string {
		return str
			.split('-')
			.map((word, index) => {
				word = word.toLowerCase()
				return index === 0 ? word : word[0].toUpperCase() + word.slice(1)
			})
			.join('')
	}

	group = group || camelize(tagName) //labelGroup is a property of a web component to give the group and use $t(<key>) without group
	return i18n.getText(group, key)
}
